/**
 * Dispatch · Shared components & constants.
 * Consumed by per-tab subcomponents and the main Dispatch page.
 */
import React from 'react';

export const CONTAINER_TYPES = [
  { value: '20GP', label: "20' General (33m³ · 28t)" },
  { value: '40GP', label: "40' General (67m³ · 28t)" },
  { value: '40HC', label: "40' High Cube (76m³ · 28t)" },
  { value: '20REEFER', label: "20' Reefer (28m³ · 27t)" },
  { value: '40REEFER', label: "40' Reefer (58m³ · 28t)" },
  { value: 'TRUCK', label: 'Truck (90m³ · 24t)' },
  { value: 'LCL', label: 'LCL (15m³ · 10t)' },
];

export const LOAD_TYPES = [
  { value: 'customer_order', label: 'Customer Order' },
  { value: 'export', label: 'Export' },
  { value: 'transfer', label: 'Transfer' },
  { value: 'replenishment', label: 'Replenishment' },
];

export const STATUS_META = {
  planned:    { label: 'Planned',    color: 'bg-muted text-foreground' },
  confirmed:  { label: 'Confirmed',  color: 'bg-blue-100 text-blue-700' },
  at_yard:    { label: 'At Yard',    color: 'bg-indigo-100 text-indigo-700' },
  at_dock:    { label: 'At Dock',    color: 'bg-cyan-100 text-cyan-700' },
  loading:    { label: 'Loading',    color: 'bg-amber-100 text-amber-800' },
  loaded:     { label: 'Loaded',     color: 'bg-yellow-100 text-yellow-800' },
  sealed:     { label: 'Sealed',     color: 'bg-emerald-100 text-emerald-800' },
  dispatched: { label: 'Dispatched', color: 'bg-teal-100 text-teal-800' },
  cancelled:  { label: 'Cancelled',  color: 'bg-rose-100 text-rose-700' },
  on_hold:    { label: 'On Hold',    color: 'bg-orange-100 text-orange-800' },
};

export const NEXT_STATUS = {
  planned: 'confirmed',
  confirmed: 'at_yard',
  at_yard: 'at_dock',
  at_dock: 'loading',
  loading: 'loaded',
  loaded: 'sealed',
  sealed: 'dispatched',
};

export const DashCard = ({ label, value, icon: Icon, accent = 'slate' }) => {
  const bgMap = {
    slate: 'text-foreground',
    amber: 'text-amber-700',
    emerald: 'text-emerald-700',
    orange: 'text-orange-700',
    blue: 'text-blue-700',
    rose: 'text-rose-700',
  };
  return (
    <div className="bg-card border border-border rounded-lg p-3">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground font-bold">
        <Icon size={12} weight="bold" />
        {label}
      </div>
      <div className={`text-2xl font-bold mt-1 ${bgMap[accent]}`}>{value}</div>
    </div>
  );
};

export const SumItem = ({ label, value, sub, accent = 'slate' }) => {
  const c =
    accent === 'emerald' ? 'text-emerald-700' :
    accent === 'rose'    ? 'text-rose-700' :
    accent === 'blue'    ? 'text-brand' :
    accent === 'amber'   ? 'text-amber-700' :
    'text-foreground';
  return (
    <div>
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">{label}</p>
      <p className={`text-lg font-bold font-mono ${c}`}>{value}</p>
      {sub && <p className="text-[10px] text-muted-foreground font-mono">{sub}</p>}
    </div>
  );
};

export const SummaryBar = ({ summary }) => {
  if (!summary) return null;
  const util = Math.max(summary.weight_pct || 0, summary.volume_pct || 0);
  return (
    <div className="bg-muted border border-border p-3 mt-2">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <SumItem label="Allocated units" value={summary.total_allocated_units} />
        <SumItem label="Loaded units" value={summary.total_loaded_units} accent="emerald" />
        <SumItem label="Weight" value={`${summary.total_weight_kg} / ${summary.max_weight_kg} kg`} sub={`${summary.weight_pct}%`} />
        <SumItem label="Volume" value={`${summary.total_volume_m3} / ${summary.max_volume_m3} m³`} sub={`${summary.volume_pct}%`} />
        <SumItem label="Utilisation" value={`${util.toFixed(0)}%`} accent={util > 95 ? 'rose' : util > 80 ? 'emerald' : 'slate'} />
      </div>
      <div className="mt-2 h-1 bg-slate-200 relative overflow-hidden">
        <div
          className={`h-full ${util > 95 ? 'bg-rose-500' : util > 80 ? 'bg-emerald-500' : 'bg-brand'}`}
          style={{ width: `${Math.min(100, util)}%` }}
          data-testid="util-bar"


        />
      </div>
    </div>
  );
};
